import './OfferHeader.scss';
import React from 'react';
import clsx from 'clsx';
import Tooltip from '@srm/ui/tooltip';
import { MdBlock, MdCancel, MdCheckCircle, MdFlag, MdGppBad, MdGppGood, MdGppMaybe } from 'react-icons/md';
import {
    IComparisonFieldSetting,
    IOffer,
    IOfferAction,
    ITableDemandSettings,
} from '@models/winnerSelection/ITableDemand';
import { formatPrice } from '@utils/helpers';
import { rankLabel } from '../utils/formatters';
import { DeliveryPie } from './DeliveryPie';
import { DecisionBar } from './DecisionBar';
import { ComparisonFieldStat } from './ComparisonFieldStat';

interface IProps {
    offer: IOffer;
    settings: ITableDemandSettings;
    /** Признак «победитель всей колонки» — для aurora-ribbon класса. */
    isTotalWinner: boolean;
    /** Признак «лидер по цене» (по рублёвой цене сравнения) — для подсветки значения. */
    isPriceWinner: boolean;
    /** Минимальная рублёвая цена сравнения среди активных предложений — для delta %. */
    minComparePrice: number | null;
    /** Максимальный summaryScore — для delta от лидера. */
    maxSummary: number | null;
    /** Выбранные пользователем дополнительные показатели. */
    comparisonFields: IComparisonFieldSetting[];
    /** Эталон min/max/target-distance для каждого дополнительного показателя. */
    comparisonBenchmarks: Record<string, number | null>;
    /** Открыть модалку выбора победителя («Оценить») из полосы-решения.
     *  undefined → полоса пассивна (используется в контекстах без модалок). */
    onSelectWinner?: (offer: IOffer, action: IOfferAction) => void;
}

/**
 * Шапка колонки одного предложения (140-строчный блок JSX вынесен из TableDemand).
 *
 * Структура: rank-кружок + лейбл (Лидер/Второй/Третий) + chip допуска ко 2 эт.
 *            + kebab-кнопка меню действий (открывается обёрткой
 *              OfferHeaderMenuDialog в parent'е);
 *            строка имени с флагом чёрного списка;
 *            pill'ы (№/тип, result, Отозвано);
 *            stat'ы (цена с delta % к мин., рейтинг с delta от лидера).
 */
export const OfferHeader: React.FC<IProps> = ({
    offer,
    settings,
    isTotalWinner,
    isPriceWinner,
    minComparePrice,
    maxSummary,
    comparisonFields,
    comparisonBenchmarks,
    onSelectWinner,
}) => {
    const rankNum = offer.rank != null && offer.rank !== '' ? Number(offer.rank) : null;
    const rankClass = rankNum != null && rankNum >= 1 && rankNum <= 3 ? `ws-offer-head__rank--${rankNum}` : '';

    // Оценка (назначение победителя) доступна, только если у предложения есть
    // видимый RateOffer-action. По правам доступа он может отсутствовать или быть
    // невидимым — тогда полоса-решение показывается пассивной (без кнопки).
    const rateAction = offer.actions.find((action) => action.type === 'RateOffer');
    const canRate = !!rateAction?.visible;
    // Тип предложения (offer.type): «Основное» / «Альтернативное». Альтернативное —
    // редкий случай, отмечаем мягкой фиолетовой точкой-маркером (см. .scss).
    const isAlternative = offer.type?.trim().toLowerCase().startsWith('альтернатив') ?? false;
    const typeName = offer.type?.toLowerCase() ?? '';

    // Лучший показатель в столбце — единая aurora-подсветка значения (см. .scss).
    const isBestRating = !offer.isWithdrawn && maxSummary != null && offer.summaryScore === maxSummary;
    // Лучшая цена — по рублёвой цене сравнения (comparePrice), независимо от валюты
    // предложения. isPriceWinner уже инкапсулирует «минимум среди активных».
    const isBestPrice = !offer.isWithdrawn && isPriceWinner;

    // Рублёвый эквивалент для сравнения показываем в тултипе цены, только если
    // отображаемая (валютная) цена отличается от рублёвой цены сравнения. При
    // единственном PriceField они равны — не дублируем.
    const showRubCompare = offer.comparePrice != null && offer.comparePrice !== offer.price;
    const priceTooltip =
        `Цена предложения\nВалюта: ${offer.currencyText ?? ''} (${offer.currencyCode})` +
        (showRubCompare ? `\nДля сравнения (в рублях): ${formatPrice(offer.comparePrice, 'RUB')}` : '');

    return (
        <div className={clsx('ws-offer-head', { 'ws-offer-head--winner': isTotalWinner })}>
            <div className="ws-offer-head__top">
                <div className={clsx('ws-offer-head__rank', rankClass)}>
                    <span className="ws-offer-head__rank-num">{rankNum ?? '—'}</span>
                    {rankNum && rankLabel(rankNum) && (
                        <span className="ws-offer-head__rank-label">{rankLabel(rankNum)}</span>
                    )}
                    {offer.approveToStageTwo === 1 ? (
                        <Tooltip openDelay={100} background="black" position="bottom" label="Допущен ко 2 этапу">
                            <span className="ws-offer-head__approve ws-offer-head__approve--ok">
                                <MdCheckCircle size="16px" /> 2 эт.
                            </span>
                        </Tooltip>
                    ) : offer.approveToStageTwo === 0 ? (
                        <Tooltip openDelay={100} background="black" position="bottom" label="Не допущен ко 2 этапу">
                            <span className="ws-offer-head__approve ws-offer-head__approve--no">
                                <MdCancel size="16px" /> 2 эт.
                            </span>
                        </Tooltip>
                    ) : null}
                    {offer.isInBlackList && settings.blackListHint ? (
                        <Tooltip openDelay={100} background="black" position="bottom" label={settings.blackListHint}>
                            <span className="ws-offer-head__blacklist">
                                <MdFlag size="13px" />
                            </span>
                        </Tooltip>
                    ) : null}
                    {offer.securityConclusion === '1' ||
                    offer.securityConclusion === '0' ||
                    offer.securityConclusion === '2' ? (
                        <Tooltip
                            openDelay={100}
                            background="black"
                            position="bottom"
                            label={
                                offer.securityConclusion === '1'
                                    ? 'Заключение СБ: Допущен'
                                    : offer.securityConclusion === '0'
                                      ? 'Заключение СБ: Не допущен'
                                      : 'Заключение СБ: Требуется дополнительная информация'
                            }
                        >
                            <span
                                className={clsx('ws-offer-head__security', {
                                    'ws-offer-head__security--ok': offer.securityConclusion === '1',
                                    'ws-offer-head__security--no': offer.securityConclusion === '0',
                                    'ws-offer-head__security--maybe': offer.securityConclusion === '2',
                                })}
                            >
                                {offer.securityConclusion === '1' ? (
                                    <MdGppGood size="16px" />
                                ) : offer.securityConclusion === '0' ? (
                                    <MdGppBad size="16px" />
                                ) : (
                                    <MdGppMaybe size="16px" />
                                )}
                            </span>
                        </Tooltip>
                    ) : null}
                    <DeliveryPie
                        quantity={offer.quantity}
                        positionQuantity={offer.positionQuantity}
                        unit={offer.unit}
                    />
                </div>
                {/* Тип предложения — тихая двухстрочная метка в правом верхнем углу
                    строки ранга: «предложение №N» + слово типа с точкой-маркером
                    (серая — основное, фиолетовая — альтернативное). Второстепенная
                    метка, не путать с решением (полосой ниже). */}
                <Tooltip
                    openDelay={150}
                    background="black"
                    position="bottom"
                    label={`Тип предложения поставщика: ${typeName || '—'}`}
                >
                    <span className="ws-offer-head__type">
                        <span className="ws-offer-head__type-id">предложение №{offer.number}</span>
                        <span
                            className={clsx('ws-offer-head__type-name', {
                                'ws-offer-head__type-name--alt': isAlternative,
                            })}
                        >
                            <span className="ws-offer-head__type-dot" aria-hidden />
                            {typeName}
                        </span>
                    </span>
                </Tooltip>
                {/* Отдельной кнопки действий нет: кликабельна вся шапка колонки
                    (OfferHeaderMenuDialog: кнопка меню во всю шапку). Что шапка
                    кликабельна, подсказывают курсор-рука и aurora-кольцо при hover. */}
            </div>

            <div className="ws-offer-head__name">
                <span className="ws-offer-head__name-text" title={offer.name}>
                    {offer.name}
                </span>
            </div>

            {offer.isWithdrawn ? (
                <div className="ws-offer-head__sub ws-offer-head__sub--pinned">
                    <span className="ws-pill ws-pill--withdrawn">
                        <MdBlock size="13px" aria-hidden />
                        Отозвано
                    </span>
                </div>
            ) : (
                <DecisionBar offer={offer} canRate={canRate} rateAction={rateAction} onSelectWinner={onSelectWinner} />
            )}

            <div className="ws-offer-head__stats">
                {settings.priceVisible && offer.price != null ? (
                    <div className={clsx('ws-stat', { 'ws-stat--best': isBestPrice })}>
                        <div className="ws-stat__label">Цена</div>
                        <div className="ws-stat__value">
                            <Tooltip openDelay={100} background="black" position="bottom" label={priceTooltip}>
                                <span>{formatPrice(offer.price, offer.currencyCode)}</span>
                            </Tooltip>
                        </div>
                        {offer.isWithdrawn ? (
                            <div className="ws-stat__delta">—</div>
                        ) : isBestPrice ? (
                            <Tooltip
                                openDelay={100}
                                background="black"
                                position="bottom"
                                label={
                                    'Лучшая цена среди всех предложений.\nСравнение ведётся по цене предложения в рублях, независимо от валюты предложения.'
                                }
                            >
                                <div className="ws-stat__delta ws-stat__delta--best">лучшая</div>
                            </Tooltip>
                        ) : offer.comparePrice != null && minComparePrice != null && minComparePrice > 0 ? (
                            <Tooltip
                                openDelay={100}
                                background="black"
                                position="bottom"
                                label={
                                    'Отклонение от минимальной цены.\nСравнение ведётся по цене предложения в рублях, независимо от валюты предложения.'
                                }
                            >
                                <div className="ws-stat__delta">
                                    +{Math.round(((offer.comparePrice - minComparePrice) / minComparePrice) * 100)}% к
                                    мин.
                                </div>
                            </Tooltip>
                        ) : (
                            /* Сравнивать не с чем — строку всё равно держим, иначе
                               разделитель этой колонки встанет выше соседних. */
                            <div className="ws-stat__delta" />
                        )}
                    </div>
                ) : (
                    <div />
                )}
                {!settings.hideSummary && offer.summaryScore != null ? (
                    <div className={clsx('ws-stat', { 'ws-stat--best': isBestRating })}>
                        <div className="ws-stat__label">Рейтинг</div>
                        <div className="ws-stat__value">
                            {offer.summaryScore.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
                        </div>
                        {offer.isWithdrawn ? (
                            <div className="ws-stat__delta">—</div>
                        ) : maxSummary != null && offer.summaryScore === maxSummary ? (
                            <div className="ws-stat__delta ws-stat__delta--leader">лидер</div>
                        ) : maxSummary != null ? (
                            <div className="ws-stat__delta">
                                −
                                {(maxSummary - offer.summaryScore).toLocaleString('ru-RU', {
                                    maximumFractionDigits: 2,
                                })}
                            </div>
                        ) : (
                            <div className="ws-stat__delta" />
                        )}
                    </div>
                ) : null}
                {comparisonFields.map((field) => (
                    <ComparisonFieldStat
                        key={field.key}
                        field={field}
                        value={offer.comparisonFields?.[field.key]}
                        benchmark={comparisonBenchmarks[field.key] ?? null}
                        isWithdrawn={offer.isWithdrawn}
                    />
                ))}
            </div>
        </div>
    );
};
